import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './TabBar.css';

/** Odhad rozměru nabídky — podle něj se drží u okraje obrazovky. */
const MENU_SIRKA = 210;
const MENU_VYSKA = 120;

function TabBar({ tabs, activeTab, setActiveTab, closeTab, closeOtherTabs, closeTabsToRight }) {
  const navigate = useNavigate();

  /** Nabídka po pravém tlačítku: `{ tabId, x, y }`, nebo `null` když je zavřená. */
  const [menu, setMenu] = useState(null);

  const zavriMenu = useCallback(() => setMenu(null), []);

  // Nabídka zmizí na kliknutí vedle, na Escape i při rolování — jinak by
  // zůstala viset na místě, kde už žádná záložka není.
  useEffect(() => {
    if (!menu) return undefined;

    const naKlavesu = (e) => { if (e.key === 'Escape') zavriMenu(); };
    document.addEventListener('mousedown', zavriMenu);
    document.addEventListener('keydown', naKlavesu);
    window.addEventListener('resize', zavriMenu);
    window.addEventListener('scroll', zavriMenu, true);

    return () => {
      document.removeEventListener('mousedown', zavriMenu);
      document.removeEventListener('keydown', naKlavesu);
      window.removeEventListener('resize', zavriMenu);
      window.removeEventListener('scroll', zavriMenu, true);
    };
  }, [menu, zavriMenu]);

  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
    navigate(tab.path);
  };

  const handleCloseTab = (e, tabId) => {
    e.stopPropagation();
    closeTab(tabId);
  };

  const handleCloseOthers = () => {
    // Keep the currently active tab open (+ dashboard)
    closeOtherTabs(activeTab);
  };

  const otevriMenu = (e, tabId) => {
    e.preventDefault();
    setMenu({
      tabId,
      x: Math.min(e.clientX, window.innerWidth - MENU_SIRKA),
      y: Math.min(e.clientY, window.innerHeight - MENU_VYSKA),
    });
  };

  const spust = (akce) => () => {
    zavriMenu();
    akce();
  };

  const menuIndex = menu ? tabs.findIndex(tab => tab.id === menu.tabId) : -1;
  const maNapravo = menuIndex >= 0 && menuIndex < tabs.length - 1;
  // „Ostatní" jsou všechny kromě téhle a Dashboardu, který zůstává vždycky.
  const maOstatni = menu ? tabs.some(tab => tab.id !== menu.tabId && tab.id !== 'dashboard') : false;

  return (
    <nav className="tabs-bar">
      <div className="tabs-container">
        {tabs.map(tab => (
          <div
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabClick(tab)}
            onContextMenu={(e) => otevriMenu(e, tab.id)}
          >
            <span className="tab-title">{tab.title}</span>
            {tabs.length > 1 && (
              <button
                className="tab-close"
                onClick={(e) => handleCloseTab(e, tab.id)}
                title="Zavřít záložku"
                aria-label={`Zavřít ${tab.title}`}
              >
                <i className="bi bi-x" />
              </button>
            )}
          </div>
        ))}
      </div>

      {tabs.length > 2 && (
        <div className="tabs-actions">
          <button
            className="btn btn-sm btn-outline-secondary"
            type="button"
            onClick={handleCloseOthers}
          >
            Zavřít ostatní
          </button>
        </div>
      )}

      {menu && (
        <div
          className="tab-menu"
          style={{ left: menu.x, top: menu.y }}
          // Nabídka se zavírá na `mousedown` kdekoliv v dokumentu; uvnitř
          // nabídky se tomu musí zabránit, jinak by zmizela dřív, než klik
          // doběhne k položce.
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            type="button"
            className="tab-menu-item"
            disabled={tabs.length <= 1}
            onClick={spust(() => closeTab(menu.tabId))}
          >
            <i className="bi bi-x-lg" />Zavřít
          </button>
          <button
            type="button"
            className="tab-menu-item"
            disabled={!maNapravo}
            onClick={spust(() => closeTabsToRight(menu.tabId))}
          >
            <i className="bi bi-arrow-bar-right" />Zavřít všechny napravo
          </button>
          <button
            type="button"
            className="tab-menu-item"
            disabled={!maOstatni}
            onClick={spust(() => closeOtherTabs(menu.tabId))}
          >
            <i className="bi bi-x-circle" />Zavřít všechny ostatní
          </button>
        </div>
      )}
    </nav>
  );
}

export default TabBar;
